import { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { FocusRingVariants } from '@calm-web/design-system';

import { type AuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import dtcPartnershipMessages from '@/hooks/dtcLeadGenPartnership/useDTCPartners/messages';
import { useFormattedCurrency } from '@/hooks/purchase/useFormattedCurrency';
import { usePricesState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';

import { PartnerData } from '../shared/types';
import messages from './messages';
import {
	AccountText,
	BlueLine,
	ButtonWrapper,
	D2CToSText,
	WelcomeButton,
	WelcomeSignUpButton,
	WelcomeText,
} from './styles';

type CalmAccountQuestionProps = {
	activationMessage: MessageDescriptor | string | null;
	compact?: boolean;
	onCtaClick: (mode: AuthPrompt) => void;
	partnerId: string;
	partnerName: string;
	partnerDetails: PartnerData;
};

export const CalmAccountQuestion = ({
	activationMessage,
	compact,
	onCtaClick,
	partnerId,
	partnerName,
	partnerDetails,
}: CalmAccountQuestionProps) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { pageInfo, offer_details, category } = partnerDetails;
	const [firstTimeSubOnly, setFirstTimeSubOnly] = useState(true);
	const prices = usePricesState();
	const formatCurrency = useFormattedCurrency();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'DTC Redemption : Welcome : Landed',
			eventProps: {
				partner_id: partnerId,
				...(category && { partner_type: [category.toLowerCase()] }),
			},
		});
		setFirstTimeSubOnly(category !== 'BRAND_PARTNERSHIP');
	});

	const renewalTerms =
		offer_details?.d2c_subscription_plan === 'monthly'
			? messages.dtcPartnershipAutoRenewMonthly
			: messages.dtcPartnershipAutoRenewYearly;

	const partnerSpecificTerms =
		pageInfo?.partnerTerms?.[pageInfo.partnerTerms?.length - 1] === '.'
			? `${pageInfo.partnerTerms} `
			: `${pageInfo?.partnerTerms}. `;

	return (
		<>
			<WelcomeText compact={compact}>
				{activationMessage &&
					(typeof activationMessage === 'string'
						? activationMessage
						: formatMessage(activationMessage, {
								partnerName,
								breakingLine: <br />,
								discountPercent: offer_details?.percent_off,
								duration: offer_details?.trial_duration
									? formatMessage(dtcPartnershipMessages[offer_details.trial_duration])
									: null,
								interval: offer_details?.billing_plan?.includes('1m')
									? formatMessage(messages.month, { quantity: offer_details?.duration_in_months || 1 })
									: formatMessage(messages.year, {
											quantity: offer_details?.duration_in_months
												? Math.ceil((offer_details?.duration_in_months as number) / 12)
												: 1,
										}),
							}))}
			</WelcomeText>
			<BlueLine />
			<AccountText>{formatMessage(messages.freeAccount)}</AccountText>
			<ButtonWrapper>
				<WelcomeButton
					onPress={(): void => onCtaClick('login')}
					data-testid="b2b-login"
					focusRingVariant={FocusRingVariants.Dark}
				>
					{formatMessage(messages.logIn)}
				</WelcomeButton>
				<WelcomeSignUpButton
					fullWidth
					backgroundColor="gradientCalmBrand"
					textColor="white"
					onPress={(): void => onCtaClick('signup')}
					data-testid="b2b-signup"
					focusRingVariant={FocusRingVariants.Dark}
					contentAlignment="left"
				>
					{formatMessage(messages.signUpFree)}
				</WelcomeSignUpButton>
			</ButtonWrapper>
			<D2CToSText>
				{firstTimeSubOnly && formatMessage(messages.dtcPartnershipCalmTerms)}{' '}
				{pageInfo?.partnerTerms && partnerSpecificTerms}
				{formatMessage(messages.dtcPartnershipOfferValidThrough, {
					date: pageInfo.endDate,
				})}{' '}
				{offer_details?.d2c_subscription_plan &&
					formatMessage(renewalTerms, {
						originalPrice: formatCurrency(prices.original[offer_details?.d2c_subscription_plan]),
					})}{' '}
				{formatMessage(messages.dtcPartnershipRedemptionLimit)}{' '}
				<strong>
					<u>
						<a href="/terms" target="_blank">
							{formatMessage(messages.dtcPartnershipTermsAndConditions)}
						</a>
					</u>
				</strong>
			</D2CToSText>
		</>
	);
};
